"use client";

/**
 * useSavedAccounts
 * ─────────────────
 * Stores up to 5 recently-signed-in BB accounts in localStorage so users
 * can quickly switch between them without re-entering credentials.
 *
 * Storage key: "bb_saved_accounts"
 * Shape: SavedAccount[]  (sorted newest-first)
 */

import { useState, useEffect, useCallback } from "react";

export interface SavedAccount {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  providerId: string; // "google.com" | "password" | etc.
  lastUsed: number;   // Date.now() timestamp
}

const KEY = "bb_saved_accounts";
const MAX = 5;

function read(): SavedAccount[] {
  if (typeof localStorage === "undefined") return [];
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? "[]");
  } catch {
    return [];
  }
}

function write(accounts: SavedAccount[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(accounts));
  } catch { /* storage full — ignore */ }
}

/** Add or update an account. Call this right after a successful sign-in. */
export function saveAccount(user: {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  providerData?: { providerId: string }[];
}) {
  const providerId = user.providerData?.[0]?.providerId ?? "password";
  const existing = read().filter(a => a.uid !== user.uid);
  const updated: SavedAccount = {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
    providerId,
    lastUsed: Date.now(),
  };
  write([updated, ...existing].slice(0, MAX));
}

/** Remove a specific saved account (e.g. after explicit "forget this account"). */
export function removeSavedAccount(uid: string) {
  write(read().filter(a => a.uid !== uid));
}

/** React hook — returns saved accounts array and helpers. Re-renders on change. */
export function useSavedAccounts() {
  const [accounts, setAccounts] = useState<SavedAccount[]>([]);

  useEffect(() => {
    setAccounts(read());
    // Sync across tabs
    const handler = (e: StorageEvent) => {
      if (e.key === KEY) setAccounts(read());
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const remove = useCallback((uid: string) => {
    removeSavedAccount(uid);
    setAccounts(read());
  }, []);

  const refresh = useCallback(() => setAccounts(read()), []);

  return { accounts, remove, refresh };
}
